import React, { FC, useState } from "react";
import { NavLink } from "react-router-dom";

import {
  Container,
  IconButton,
  Paper,
  Popover,
  Typography,
} from "@mui/material";
import {
  GridColDef,
  GridEventListener,
  GridFilterModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";

import DataGridPro from "../../components/DataGridPro";
import DataGridProToolbar from "../../components/DataGridProToolbar";
import { ChangesetIcon, ViewIcon } from "../../components/icons";
import useClusterColumn from "../../hooks/use-cluster-column";
import useIdColumn from "../../hooks/use-id-column";
import usePaginationModel from "../../hooks/use-pagination-model";
import { useSavedColumnWidths } from "../../hooks/use-saved-column-widths";
import { useSelector } from "../../store";
import {
  getFilter,
  getOrderBy,
  useListChangesetsQuery,
} from "../../store/enhancedApi";
import { useApplyChangesetMutation } from "../../store/pomeriumApi";
import { createdAt, updatedAt } from "../../utils/data-grid-col-defs";

const ChangesetsList: FC = () => {
  const clusterCol = useClusterColumn();
  const idCol = useIdColumn();
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<string>(
    localStorage.getItem("deploymentsColumns") || "{}",
  );
  const { mutateColumnWidths, onColumnWidthChange } =
    useSavedColumnWidths("changesets");
  const [applyChangeset] = useApplyChangesetMutation();
  const [popoverCell, setPopoverCell] = React.useState<HTMLElement | null>(
    null,
  );
  const [paginationModel, setPaginationModel] = usePaginationModel();
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const {
    data: result,
    isLoading,
    isFetching,
  } = useListChangesetsQuery(
    {
      organizationId,
      clusterId,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      filter: getFilter(filterModel),
      orderBy: getOrderBy(sortModel),
    },
    { skip: !organizationId || !clusterId },
  );
  const deployments = result?.rows || [];
  const rowCount = result?.rowCount || 0;
  const estimatedRowCount = result?.estimatedRowCount || 0;
  const hasPendingChanges =
    (deployments?.filter((d) => d.status === "pending") || []).length > 0;

  const handleCellClick: GridEventListener<"cellClick"> = (params, event) => {
    if (
      params.field === "failureMessage" &&
      event.currentTarget instanceof HTMLElement
    ) {
      setPopoverCell(event.currentTarget);
    }
  };

  const clickApplyChangeset = async (id: string) => {
    await applyChangeset({
      organizationId: organizationId,
      changesetId: id,
    });
  };

  let columns: GridColDef[] = [
    idCol,
    clusterCol,
    {
      headerName: "Status",
      field: "status",
      flex: 1,
    },
    {
      headerName: "Errors",
      field: "failureMessage",
      flex: 1,
    },
    updatedAt,
    createdAt,
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: hasPendingChanges ? "left" : "center",
      align: hasPendingChanges ? "left" : "center",
      sortable: false,
      filterable: false,
      type: "actions",
      flex: 1,
      getActions: function action(params) {
        const actions = [
          <IconButton
            key={"details" + params.row.id}
            component={NavLink}
            sx={{ mr: 2 }}
            to={{
              pathname:
                "/app/reports/deployments/" + params.row.id + "/compare",
            }}
            size="small"
          >
            <ViewIcon />
          </IconButton>,
        ];
        if (params.row.status === "pending") {
          actions.push(
            <IconButton
              onClick={() => clickApplyChangeset(params.row.id)}
              key={"apply" + params.row.id}
              sx={{ mr: 2 }}
              size="small"
            >
              <ChangesetIcon />
            </IconButton>,
          );
        }
        return actions;
      },
    },
  ];
  columns = mutateColumnWidths(columns);

  return (
    <>
      <Container component={Paper} maxWidth={false} disableGutters={true}>
        <DataGridPro
          checkboxSelection={false}
          loading={isLoading || isFetching}
          rows={clusterId ? deployments || [] : []}
          columns={columns}
          columnVisibilityModel={JSON.parse(columnVisibilityModel)}
          onCellClick={handleCellClick}
          onColumnVisibilityModelChange={(newModel) => {
            localStorage.setItem(
              "deploymentsColumns",
              JSON.stringify(newModel),
            );
            setColumnVisibilityModel(JSON.stringify(newModel));
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: "updatedAt", sort: "desc" }],
            },
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          slots={{
            toolbar: DataGridProToolbar,
          }}
          onColumnWidthChange={onColumnWidthChange}
          paginationMode="server"
          rowCount={rowCount}
          estimatedRowCount={estimatedRowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          onSortModelChange={setSortModel}
          onFilterModelChange={setFilterModel}
        />
      </Container>
      <Popover
        anchorEl={popoverCell}
        open={Boolean(popoverCell)}
        onClose={() => setPopoverCell(null)}
      >
        <Typography sx={{ m: 1 }}>{popoverCell?.textContent}</Typography>
      </Popover>
    </>
  );
};
export default ChangesetsList;
